/**
 * useDataSet — 행 트래킹 + 변경 추적 + 검증 훅 (vue-spring-fw 의 composable 패리티).
 *
 * 【역할】
 * - 서버 조회 → 편집 → 변경분 추출 → 저장 전 validate 의 전체 수명주기 관리.
 * - vue-spring-fw (Vue 3 + Pinia) 의 `useDataSet` composable 를 React hook 으로 1:1 이식.
 * - 페이지 코드는 이 훅 하나로 grid/form 의 상태·검증·직렬화를 모두 처리한다.
 *
 * 【주요 기능】
 * - 행 조작: load, addRow, updateRow, setCell, deleteRow, revertRow, clearDeleted, clear
 * - 추출: getChanges (C/U/D 분리), getVisibleRows (D 제외), getAllRows, getChangedRows
 * - 협업: copyFrom, mergeFrom (다른 dataset 의 값 주입/병합)
 * - 북마크: bookmark (재조회 후 pkField 기반 선택 자동 복원)
 * - 검증: validate (string-rule + custom + cross-field 3 단계)
 * - 잠금: lockOnUpdate (U 상태에서 수정 금지 필드 지정)
 *
 * 【추적 모델】
 * - 각 행에 _state(C/U/D/''), _rowId, _original 스냅샷 보유.
 * - load() 후 updateRow/setCell 로 _original 대비 변경 시 자동 'U'.
 * - addRow → 'C', deleteRow → C 즉시 제거 / 그 외 'D'.
 * - revertRow → C 삭제 / U 는 _original 에서 복원하여 '' 로 환원.
 *
 * 【사용 예시】
 * ```tsx
 * const ds = useDataSet<UserRow>([], { pkField: 'userId' })
 * ds.load(rowsFromServer)
 * ds.addRule('userName', t('COL_USER_NAME'), 'required,maxlength=50')
 * ds.setCell(0, 'userName', 'Alice')
 * const { valid, errors } = ds.validate()
 * if (valid) await $transaction({ id: 'saveUsers', service: 'user', inds: { ds_user: ds } })
 * ```
 *
 * 【왜 이 패턴?】
 * - Ant Design Form 만으로는 N 행 동시 편집 / 변경분만 서버 전송이 번거롭다.
 * - vue-spring-fw 원본 composable 을 그대로 가져와 Vue 팀의 학습비용을 0 으로 유지.
 * - immer 기반 produce 로 불변성 보장 + 필드 단위 diff 로 최소 리렌더.
 */
import { useCallback, useMemo, useRef, useState } from 'react'
import i18n from '@/i18n'

/**
 * i18n 키 + 기본 한글 + {label}/{n} placeholder 치환.
 * @param key - i18n 메시지 키 (예: MSG_VALIDATE_REQUIRED).
 * @param fallback - 번역 미존재 시 사용할 한글 기본 문자열.
 * @param label - {label} placeholder 에 주입될 필드 라벨.
 * @param n - {n} placeholder 에 주입될 숫자 (minlength/maxlength 용).
 * @returns 치환 완료된 사용자 표시 메시지.
 */
function vmsg(key: string, fallback: string, label: string, n?: number): string {
  const raw = i18n.t(key, { defaultValue: fallback }) as string
  let out = raw.replace(/\{label\}/g, label)
  if (n !== undefined) out = out.replace(/\{n\}/g, String(n))
  return out
}
import { produce } from 'immer'

/** 행 상태: C=신규(Create), U=수정(Update), D=삭제(Delete), ''=변경없음. */
export type RowState = 'C' | 'U' | 'D' | ''

/** 내부 추적 행 — data 는 사용자 데이터, _original 은 load 직후 스냅샷. */
export interface TrackedRow<T = Record<string, unknown>> {
  _state: RowState
  _rowId: string
  data: T
  _original?: T
}

/** getChanges 결과 — 서버 저장 RPC 에 전달될 C/U/D 분리 컬렉션. */
export interface DataSetChanges<T> {
  inserted: T[]
  updated: T[]
  deleted: T[]
}

/** 문자열 기반 검증 룰 — 하나의 필드에 여러 토큰을 쉼표로 지정. */
export interface ValidationRule {
  field: string
  label: string
  /** "required,number,minlength=N,maxlength=N,regex=PATTERN" — 쉼표 구분 */
  rules: string
}

/** 단일 필드 커스텀 검증 — null 반환 시 통과, 문자열 반환 시 에러 메시지. */
export type CustomValidator<T = Record<string, unknown>> = (value: unknown, row: T) => string | null
/** 행 전체 필드를 보고 판정하는 검증 — null 또는 {valid:true} 시 통과. */
export type CrossFieldValidator<T = Record<string, unknown>> = (
  row: T
) => { valid: boolean; message: string } | null

/** validate 결과 항목 — field/label 은 cross-field 의 경우 빈 문자열. */
export interface ValidationError {
  field: string
  label: string
  message: string
}

/** useDataSet 훅 생성 옵션. */
export interface UseDataSetOptions<T> {
  pkField?: keyof T & string
  rules?: ValidationRule[]
  /** DataSet 식별 이름 (디버깅/로그) */
  name?: string
}

export interface UseDataSetResult<T> {
  // ── 상태 ──
  /** 추적 행 배열 — 각 행은 _state/_rowId/data/_original 보유 */
  rows: TrackedRow<T>[]
  /** 변경 행이 1개라도 있으면 true (저장 버튼 활성화 등에 사용) */
  isDirty: boolean
  /** 현재 선택 행 인덱스 — 선택 없음은 -1 */
  selectedIndex: number
  /** selectedIndex 의 행 객체 (없으면 null) */
  selectedRow: TrackedRow<T> | null
  /** 선택 인덱스 setter — 그리드 row click 등에서 호출 */
  setSelectedIndex: (i: number) => void

  // ── 행 조작 ──
  /** 서버 응답을 dataset 에 적재 + _original 스냅샷 확정 */
  load: (items: T[]) => void
  /** 신규 행 추가 — _state='C' */
  addRow: (data?: Partial<T>) => void
  /** 여러 필드 patch — 변경 시 자동 'U' 마킹 */
  updateRow: (index: number, patch: Partial<T>) => void
  /** Vue 원본 setCell 호환 — updateRow 의 단일 필드 버전 + onCellChange 콜백 발화 */
  setCell: (index: number, field: keyof T & string, value: unknown) => void
  /** 행 삭제 — C 즉시 제거 / 그 외 'D' 마킹 */
  deleteRow: (index: number) => void
  /** 행 변경 취소 — _original 에서 복원 */
  revertRow: (index: number) => void
  /** D 마킹 행을 배열에서 실제 제거 (저장 성공 후 후처리용) */
  clearDeleted: () => void
  /** 전체 초기화 (rows/selectedIndex/bookmark) */
  clear: () => void

  // ── 추출 ──
  /** C/U/D 분리 컬렉션 반환 — useTransaction 직렬화 입력 */
  getChanges: () => DataSetChanges<T>
  /** D 제외 표시용 행 배열 (그리드 dataSource 용) */
  getVisibleRows: () => TrackedRow<T>[]
  /** 추적 메타 제외 순수 데이터 배열 */
  getAllRows: () => T[]
  /** 변경된 행만 _state 부여한 평탄 객체 배열 */
  getChangedRows: () => Array<T & { _state: RowState }>

  // ── 다른 dataset 과 협업 ──
  /** 다른 dataset 의 행을 모두 신규(C)로 복사 — 기존 행은 폐기 */
  copyFrom: (
    source: UseDataSetResult<Record<string, unknown>>,
    fields?: (keyof T & string)[]
  ) => void
  /** matchField 기준 mergeFields 만 본 dataset 행에 덮어쓰기 — '' → 'U' 승격 */
  mergeFrom: (
    source: UseDataSetResult<Record<string, unknown>>,
    matchField: keyof T & string,
    mergeFields: (keyof T & string)[]
  ) => void

  // ── 북마크 (재조회 후 선택 복원) ──
  /** 현재 선택 행의 PK 값을 기억 — 다음 load() 시 자동 복원 */
  bookmark: (pkField?: keyof T & string) => void

  // ── 검증 ──
  /** 3 단계 검증 실행 (string-rule + custom + cross-field) */
  validate: (changedOnly?: boolean) => { valid: boolean; errors: ValidationError[] }
  /** 문자열 룰 추가/갱신 (동일 field 면 덮어씀) */
  addRule: (field: keyof T & string, label: string, rules: string) => void
  /** field 의 문자열 룰 제거 */
  removeRule: (field: keyof T & string) => void
  /** 커스텀 검증 룰 추가 — string-rule 로 표현 불가능한 동적 조건용 */
  addCustomRule: (field: keyof T & string, label: string, validator: CustomValidator<T>) => void
  /** 행 전체 필드를 보고 판정하는 cross-field 룰 추가 */
  addCrossFieldRule: (validator: CrossFieldValidator<T>) => void

  // ── 콜백 / 가드 ──
  /** setCell 발화 시 호출될 콜백 등록 — 다중 등록 가능 */
  onCellChange: (
    cb: (row: T, field: keyof T & string, newValue: unknown, oldValue: unknown) => void
  ) => void

  // ── 잠금 ──
  /** U 상태에서 편집 잠금할 필드 지정 — UI disabled 처리 시 참조 */
  lockOnUpdate: (fields: (keyof T & string)[]) => void
  /** 현재 잠금 필드 목록 사본 반환 */
  getLockedFields: () => string[]

  // ── 메타 ──
  /** dataset 식별 이름 (디버깅/로그) */
  name: string
  /** PK 필드명 — 북마크/머지 매칭에 사용 */
  pkField?: string
}

let rowSeq = 0
/** 행 고유 ID 생성기 — key prop 충돌 방지용 (Date.ms + 단조 증가 시퀀스). */
const nextId = () => `r${Date.now().toString(36)}_${(rowSeq++).toString(36)}`

/**
 * JSON 기반 깊은 복제 — _original 스냅샷 보존용.
 * 순환 참조/Date/Map 은 지원하지 않음 (평범한 DTO 용도).
 */
function deepClone<T>(v: T): T {
  if (v === null || typeof v !== 'object') return v
  return JSON.parse(JSON.stringify(v))
}

/**
 * useDataSet — 변경 추적 + 검증 훅 진입점.
 * @param initial - 초기 행 배열 (테스트/시드 용도, 보통 빈 배열 후 load 사용).
 * @param options - pkField/rules/name 지정.
 * @returns 행 조작·추출·검증 메서드와 상태 객체.
 */
export function useDataSet<T extends object = Record<string, unknown>>(
  initial: T[] = [],
  options?: UseDataSetOptions<T>
): UseDataSetResult<T> {
  const initRef = useRef<T[]>(initial)
  const pkField = options?.pkField
  const dsName = options?.name ?? 'ds'

  const [rows, setRows] = useState<TrackedRow<T>[]>(() =>
    initRef.current.map((d) => ({
      _state: '' as RowState,
      _rowId: nextId(),
      data: { ...d },
      _original: deepClone(d)
    }))
  )
  const [selectedIndex, setSelectedIndex] = useState<number>(-1)

  // ── 검증 룰 / 콜백 / 잠금 — refs (rerender 유발 안 함) ──
  const rulesRef = useRef<ValidationRule[]>(options?.rules ? [...options.rules] : [])
  const customRulesRef = useRef<
    Array<{ field: string; label: string; validator: CustomValidator<T> }>
  >([])
  const crossRulesRef = useRef<CrossFieldValidator<T>[]>([])
  const cellChangeCbsRef = useRef<
    Array<(row: T, field: keyof T & string, newValue: unknown, oldValue: unknown) => void>
  >([])
  const lockedRef = useRef<string[]>([])
  const bookmarkRef = useRef<unknown>(null)

  /**
   * 서버 응답을 dataset 에 적재 + _original 스냅샷 확정.
   * pkField + 이전 bookmark 가 있으면 동일 PK 행으로 selectedIndex 복원.
   */
  const load = useCallback(
    (items: T[]) => {
      setRows(
        items.map((d) => ({
          _state: '' as RowState,
          _rowId: nextId(),
          data: { ...d },
          _original: deepClone(d)
        }))
      )
      // 북마크 복원
      if (bookmarkRef.current != null && pkField) {
        const idx = items.findIndex(
          (r) => (r as Record<string, unknown>)[pkField] === bookmarkRef.current
        )
        setSelectedIndex(idx >= 0 ? idx : items.length > 0 ? 0 : -1)
        bookmarkRef.current = null
      } else {
        setSelectedIndex(items.length > 0 ? 0 : -1)
      }
    },
    [pkField]
  )

  /**
   * 신규 행 추가 — _state='C' 로 markup. _original 없음 (C 는 revertRow 시 완전 삭제).
   */
  const addRow = useCallback((data?: Partial<T>) => {
    setRows((prev) => [
      ...prev,
      { _state: 'C' as RowState, _rowId: nextId(), data: { ...(data as T) } }
    ])
  }, [])

  /**
   * 특정 행의 여러 필드를 patch 형태로 갱신. _original 대비 실제 변경 있을 때만 'U' 마킹.
   */
  const updateRow = useCallback((index: number, patch: Partial<T>) => {
    setRows((prev) =>
      produce(prev, (draft) => {
        const row = draft[index]
        if (!row) return
        Object.assign(row.data, patch)
        if (row._state === '' && row._original) {
          // 변경 감지: 패치된 필드 중 _original 과 다른 것 있으면 'U'
          const changed = Object.keys(patch).some(
            (k) =>
              (row.data as Record<string, unknown>)[k] !==
              (row._original as Record<string, unknown>)[k]
          )
          if (changed) row._state = 'U'
        }
      })
    )
  }, [])

  /**
   * 단일 필드 변경 — Vue 원본 setCell 호환 + onCellChange 콜백 발화.
   * grid inline edit 처럼 1 필드만 바뀌는 경우 updateRow 보다 선호.
   */
  const setCell = useCallback((index: number, field: keyof T & string, value: unknown) => {
    let oldValue: unknown = undefined
    let rowData: T | undefined
    setRows((prev) =>
      produce(prev, (draft) => {
        const row = draft[index]
        if (!row) return
        const dataRec = row.data as Record<string, unknown>
        oldValue = dataRec[field]
        dataRec[field] = value
        rowData = row.data as T
        if (row._state === '' && row._original) {
          if ((row._original as Record<string, unknown>)[field] !== value) row._state = 'U'
        }
      })
    )
    if (rowData) {
      cellChangeCbsRef.current.forEach((cb) => {
        try {
          cb(rowData as T, field, value, oldValue)
        } catch {
          /* noop */
        }
      })
    }
  }, [])

  /**
   * 행 삭제 — C 상태는 배열에서 즉시 제거, 그 외는 _state='D' 로 마킹.
   * D 마킹된 행은 getVisibleRows 에서 제외되지만 getChanges.deleted 로 서버 전송된다.
   */
  const deleteRow = useCallback((index: number) => {
    setRows((prev) =>
      produce(prev, (draft) => {
        const row = draft[index]
        if (!row) return
        if (row._state === 'C') {
          draft.splice(index, 1)
        } else {
          row._state = 'D'
        }
      })
    )
  }, [])

  /**
   * 행 변경 취소 — C 는 제거, U/D 는 _original 스냅샷에서 복원 후 _state=''.
   */
  const revertRow = useCallback((index: number) => {
    setRows((prev) =>
      produce(prev, (draft) => {
        const row = draft[index]
        if (!row) return
        if (row._state === 'C') {
          draft.splice(index, 1)
        } else if (row._original) {
          row.data = deepClone(row._original) as typeof row.data
          row._state = ''
        }
      })
    )
  }, [])

  /** D 마킹된 행을 배열에서 실제로 제거 — save 성공 후 후처리 용도. */
  const clearDeleted = useCallback(() => {
    setRows((prev) => prev.filter((r) => r._state !== 'D'))
  }, [])

  /** 전체 초기화 — rows/selectedIndex/bookmark 모두 리셋. */
  const clear = useCallback(() => {
    setRows([])
    setSelectedIndex(-1)
    bookmarkRef.current = null
  }, [])

  /**
   * 변경된 행을 C/U/D 로 분리해 반환 — useTransaction.serializeInds 의 핵심 입력.
   */
  const getChanges = useCallback((): DataSetChanges<T> => {
    const inserted: T[] = []
    const updated: T[] = []
    const deleted: T[] = []
    for (const r of rows) {
      if (r._state === 'C') inserted.push(r.data)
      else if (r._state === 'U') updated.push(r.data)
      else if (r._state === 'D') deleted.push(r.data)
    }
    return { inserted, updated, deleted }
  }, [rows])

  const getVisibleRows = useCallback(() => rows.filter((r) => r._state !== 'D'), [rows])

  const getAllRows = useCallback(() => rows.map((r) => r.data), [rows])

  const getChangedRows = useCallback((): Array<T & { _state: RowState }> => {
    return rows
      .filter((r) => r._state === 'C' || r._state === 'U' || r._state === 'D')
      .map((r) => ({ ...(r.data as T), _state: r._state }))
  }, [rows])

  /**
   * 다른 dataset 의 모든 행을 신규(C) 상태로 가져와 본 dataset 으로 교체.
   * @param fields - 지정 시 해당 필드만 복사, 미지정 시 전체 필드 복사.
   * 주의: 기존 행은 모두 폐기되며 일괄 'C' 상태가 된다 (저장 시 INSERT).
   */
  const copyFrom = useCallback(
    (source: UseDataSetResult<Record<string, unknown>>, fields?: (keyof T & string)[]) => {
      const sourceRows = source.getAllRows() as Record<string, unknown>[]
      const cloned: TrackedRow<T>[] = sourceRows.map((src) => {
        let payload: Record<string, unknown>
        if (fields && fields.length) {
          payload = {}
          for (const f of fields) payload[f] = src[f]
        } else {
          payload = { ...src }
        }
        return { _state: 'C' as RowState, _rowId: nextId(), data: payload as T }
      })
      setRows(cloned)
    },
    []
  )

  /**
   * 다른 dataset 에서 매칭 필드(matchField) 기준으로 mergeFields 만 본 dataset 행에 덮어쓴다.
   * @param matchField - 양쪽 dataset 행을 매칭하는 키 필드 (PK 등).
   * @param mergeFields - 매칭 시 복사할 필드 목록.
   * 행 상태 변화: 매칭되면 '' → 'U' 로 승격, 'C' 는 그대로 유지.
   */
  const mergeFrom = useCallback(
    (
      source: UseDataSetResult<Record<string, unknown>>,
      matchField: keyof T & string,
      mergeFields: (keyof T & string)[]
    ) => {
      const sourceRows = source.getAllRows() as Record<string, unknown>[]
      const map = new Map<string, Record<string, unknown>>()
      for (const sr of sourceRows) map.set(String(sr[matchField] ?? ''), sr)
      setRows((prev) =>
        produce(prev, (draft) => {
          for (const r of draft) {
            const dataRec = r.data as Record<string, unknown>
            const key = String(dataRec[matchField] ?? '')
            const matched = map.get(key)
            if (!matched) continue
            for (const f of mergeFields) dataRec[f] = matched[f]
            if (r._state !== 'C') r._state = 'U'
          }
        })
      )
    },
    []
  )

  /**
   * 현재 선택 행의 PK 값을 기억 — 다음 load() 호출 시 같은 PK 행으로 selectedIndex 자동 복원.
   * 재조회 직전 호출 → load() 가 1회 소비 후 자동으로 bookmark clear.
   * @param pk - PK 필드명 (생략 시 옵션의 pkField).
   */
  const bookmark = useCallback(
    (pk?: keyof T & string) => {
      const field = pk ?? pkField
      const r = selectedIndex >= 0 ? rows[selectedIndex] : null
      if (field && r) bookmarkRef.current = (r.data as Record<string, unknown>)[field]
    },
    [pkField, rows, selectedIndex]
  )

  // ── 검증 ──
  /**
   * 3 단계 검증 실행: (1) string-rule (2) custom (3) cross-field.
   * @param changedOnly - true(기본) 면 C/U 만, false 면 D 제외 전부 검사.
   * @returns valid 플래그 + ValidationError 배열 (errors.length===0 이면 통과).
   */
  const validate = useCallback(
    (changedOnly = true): { valid: boolean; errors: ValidationError[] } => {
      const errors: ValidationError[] = []
      const target = changedOnly
        ? rows.filter((r) => r._state === 'C' || r._state === 'U')
        : rows.filter((r) => r._state !== 'D')

      for (const r of target) {
        const data = r.data as Record<string, unknown> & T

        // 1) string rules
        for (const rule of rulesRef.current) {
          const value = data[rule.field]
          const tokens = rule.rules
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean)
          for (const tok of tokens) {
            if (tok === 'required') {
              if (value === null || value === undefined || String(value).trim() === '') {
                errors.push({
                  field: rule.field,
                  label: rule.label,
                  message: vmsg(
                    'MSG_VALIDATE_REQUIRED',
                    '{label}은(는) 필수 입력입니다.',
                    rule.label
                  )
                })
              }
            } else if (tok === 'number') {
              if (
                value !== null &&
                value !== undefined &&
                String(value).trim() !== '' &&
                Number.isNaN(Number(value))
              ) {
                errors.push({
                  field: rule.field,
                  label: rule.label,
                  message: vmsg(
                    'MSG_VALIDATE_NUMBER',
                    '{label}은(는) 숫자만 입력 가능합니다.',
                    rule.label
                  )
                })
              }
            } else if (tok.startsWith('minlength=')) {
              const n = parseInt(tok.split('=')[1], 10)
              if (value && String(value).length < n) {
                errors.push({
                  field: rule.field,
                  label: rule.label,
                  message: vmsg(
                    'MSG_VALIDATE_MINLENGTH',
                    '{label}은(는) 최소 {n}자 이상 입력해야 합니다.',
                    rule.label,
                    n
                  )
                })
              }
            } else if (tok.startsWith('maxlength=')) {
              const n = parseInt(tok.split('=')[1], 10)
              if (value && String(value).length > n) {
                errors.push({
                  field: rule.field,
                  label: rule.label,
                  message: vmsg(
                    'MSG_VALIDATE_MAXLENGTH',
                    '{label}은(는) {n}자 이내로 입력해야 합니다.',
                    rule.label,
                    n
                  )
                })
              }
            } else if (tok.startsWith('regex=')) {
              const pat = tok.substring(6)
              if (value !== null && value !== undefined && String(value).trim() !== '') {
                try {
                  if (!new RegExp(pat).test(String(value))) {
                    errors.push({
                      field: rule.field,
                      label: rule.label,
                      message: vmsg(
                        'MSG_VALIDATE_REGEX',
                        '{label}의 형식이 올바르지 않습니다.',
                        rule.label
                      )
                    })
                  }
                } catch {
                  errors.push({
                    field: rule.field,
                    label: rule.label,
                    message: vmsg(
                      'MSG_VALIDATE_REGEX_INVALID',
                      '{label}의 정규식 패턴이 잘못되었습니다.',
                      rule.label
                    )
                  })
                }
              }
            }
          }
        }

        // 2) custom rules
        for (const cr of customRulesRef.current) {
          const msg = cr.validator(data[cr.field], data)
          if (msg) errors.push({ field: cr.field, label: cr.label, message: msg })
        }

        // 3) cross-field rules
        for (const cfr of crossRulesRef.current) {
          const result = cfr(data)
          if (result && !result.valid)
            errors.push({ field: '', label: '', message: result.message })
        }
      }
      return { valid: errors.length === 0, errors }
    },
    [rows]
  )

  /**
   * 문자열 검증 룰 추가/갱신 — 동일 field 가 이미 있으면 덮어씀.
   * @param rules - 'required,maxlength=100,regex=^[A-Z]+$' 형식 (쉼표 구분).
   */
  const addRule = useCallback((field: keyof T & string, label: string, rules: string) => {
    const idx = rulesRef.current.findIndex((r) => r.field === field)
    if (idx >= 0) rulesRef.current[idx] = { field, label, rules }
    else rulesRef.current.push({ field, label, rules })
  }, [])

  /** field 에 등록된 문자열 룰 제거 (custom/cross 룰은 유지). */
  const removeRule = useCallback((field: keyof T & string) => {
    rulesRef.current = rulesRef.current.filter((r) => r.field !== field)
  }, [])

  /**
   * 커스텀 검증 룰 등록 — string-rule 로 표현 불가능한 동적 조건용.
   * @param validator - (value, row) => 에러 메시지 또는 null(통과).
   */
  const addCustomRule = useCallback(
    (field: keyof T & string, label: string, validator: CustomValidator<T>) => {
      customRulesRef.current.push({ field, label, validator })
    },
    []
  )

  /**
   * 행 전체 필드를 종합 판단하는 cross-field 룰 등록 (예: 시작일 < 종료일).
   * @param validator - (row) => { valid, message } 또는 null(통과).
   */
  const addCrossFieldRule = useCallback((validator: CrossFieldValidator<T>) => {
    crossRulesRef.current.push(validator)
  }, [])

  /**
   * setCell 발화 시 호출될 콜백 등록 — 다중 등록 가능 (push 만 하고 unregister 미지원).
   * @param cb - (row, field, newValue, oldValue) 시그니처. 콜백 내 throw 는 무시된다.
   */
  const onCellChange = useCallback(
    (cb: (row: T, field: keyof T & string, newValue: unknown, oldValue: unknown) => void) => {
      cellChangeCbsRef.current.push(cb)
    },
    []
  )

  /**
   * U(수정) 상태에서 편집 잠금할 필드 지정 — UI 측에서 disabled 적용 시 참조.
   * 주의: 본 훅은 잠금 목록만 관리. 실제 disabled 처리는 페이지/컴포넌트 책임.
   */
  const lockOnUpdate = useCallback((fields: (keyof T & string)[]) => {
    lockedRef.current = [...fields]
  }, [])

  /** 현재 잠금 필드 목록 사본 반환 — 외부에서 mutate 해도 내부 영향 없음. */
  const getLockedFields = useCallback(() => [...lockedRef.current], [])

  const isDirty = useMemo(() => rows.some((r) => r._state !== ''), [rows])
  const selectedRow = selectedIndex >= 0 ? (rows[selectedIndex] ?? null) : null

  return {
    rows,
    isDirty,
    selectedIndex,
    selectedRow,
    setSelectedIndex,
    load,
    addRow,
    updateRow,
    setCell,
    deleteRow,
    revertRow,
    clearDeleted,
    clear,
    getChanges,
    getVisibleRows,
    getAllRows,
    getChangedRows,
    copyFrom,
    mergeFrom,
    bookmark,
    validate,
    addRule,
    removeRule,
    addCustomRule,
    addCrossFieldRule,
    onCellChange,
    lockOnUpdate,
    getLockedFields,
    name: dsName,
    pkField: pkField as string | undefined
  }
}
